import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import warrantyTicketServices from '../services/warrantyTicketServices';

/**
 * Red banner shown above the Schedule page with the count of active
 * (non-Resolved) warranty tickets relevant to the current user.
 *
 * Visibility is driven entirely by the backend (`/warranty-ticket/active-count`),
 * which already applies the right per-role filter:
 *   - admins / superadmins / dispatchers → all active tickets
 *   - technicians                        → tickets they CREATED
 *
 * The banner self-hides when `count === 0`. It polls every 60s and also
 * refetches when the tab becomes visible again (so closing a ticket in
 * another tab updates the counter without a page reload).
 *
 * Clicking the banner navigates to the Warranty Tickets list page.
 */
const WarrantyTicketsBanner = () => {
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await warrantyTicketServices.getActiveCount();
      const value = Number(res?.data?.count);
      setCount(Number.isFinite(value) && value > 0 ? value : 0);
    } catch (err) {
      console.warn('[WarrantyTicketsBanner] failed to fetch active count', err);
      setCount(0);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    intervalRef.current = setInterval(fetchCount, 60_000);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchCount();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchCount]);

  if (count <= 0) return null;

  const label = count === 1 ? 'active warranty ticket' : 'active warranty tickets';

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => navigate('/warranty-tickets')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('/warranty-tickets');
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1.25,
        mb: 1.5,
        borderRadius: 1,
        backgroundColor: '#d32f2f',
        color: '#fff',
        cursor: 'pointer',
        boxShadow: '0 2px 6px rgba(211, 47, 47, 0.35)',
        transition: 'background-color 0.15s ease',
        '&:hover': { backgroundColor: '#b71c1c' },
        '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
      }}
    >
      <WarningAmberIcon sx={{ fontSize: 22 }} />
      <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1rem' } }}>
        {count} {label}
      </Typography>
      <Typography sx={{ ml: 0.5, opacity: 0.9, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
        — tap to review
      </Typography>
      <Box sx={{ flex: 1 }} />
      <ChevronRightIcon />
    </Box>
  );
};

export default WarrantyTicketsBanner;
